import { useMemo } from "react"

import {
  Box,
  IconButton,
  InputBase,
  Divider,
  useTheme,
  CardMedia,
  SxProps,
  Typography,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import ImportExportIcon from "@mui/icons-material/ImportExport"
import MoreVertIcon from "@mui/icons-material/MoreVert"

import clsx from "clsx"

import { useTranslation } from "providers/i18n"
import { getIcon } from "utils/icon"

import {
  ArrowLeftIcon,
  DestinationMarkerIcon,
  OriginMarkerIcon,
  CloseIcon,
} from "components/icons"

interface IClearButtonProps {
  sx: SxProps
  onClick: () => void
  show: boolean
  children: React.ReactNode
}

interface IDirectionSearchProps {
  originInputRef: React.RefObject<HTMLInputElement>
  destinationInputRef: React.RefObject<HTMLInputElement>
  onOriginChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onDestinationChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onOriginFocus: () => void
  onDestinationFocus: () => void
  onBack: () => void
  onSwitch: () => void
  onClearOrigin: () => void
  onClearDestination: () => void
  onClick?: () => void
  endAdornmentSlotClasses: {
    origin: string
    destination: string
  }
  endAdornmentSlots: {
    logo: {
      originLogo: string
      destinationLogo: string
    }
    name: {
      originVenueName: string
      destinationVenueName: string
    }
    chip?: {
      origin?: React.ReactNode
      destination?: React.ReactNode
    }
  }
}

const ClearButton = ({
  sx,
  onClick = () => {},
  show = false,
  children,
  ...props
}: IClearButtonProps) => (
  <IconButton
    sx={{ ...sx, visibility: show ? "visible" : "hidden" }}
    size="small"
    onClick={onClick}
    {...props}
  >
    {children}
  </IconButton>
)

export const DirectionSearch = ({
  originInputRef,
  destinationInputRef,
  onOriginChange,
  onDestinationChange,
  onOriginFocus,
  onDestinationFocus,
  onBack,
  onSwitch,
  onClearOrigin,
  onClearDestination,
  onClick,
  endAdornmentSlotClasses,
  endAdornmentSlots,
  ...props
}: IDirectionSearchProps) => {
  const theme = useTheme()
  const {
    directionSearchWrapper,
    backIconWrapper,
    directionWrapper,
    switchIconWrapper,
    directionInputWrapper,
    directionIconWrapper,
    directionInput,
    iconButton,
    backIconButton,
    switchIconButton,
    connectorIcon,
    originIcon,
    destinationIcon,
    divider,
    clearIconButton,
    endAdornmentWrapper,
    venueLogoContainer,
    venueLogo,
    venueName,
  } = theme.components[`DirectionSearchInput`]?.styleOverrides || {}

  const { t } = useTranslation()

  const showOriginClearButton = !!originInputRef?.current?.value
  const showDestinationClearButton = !!destinationInputRef?.current?.value

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <ArrowLeftIcon />,
    [theme]
  )

  const clearIcon = useMemo(
    () => getIcon(theme?.assets?.directionSearch?.clear) || <CloseIcon />,
    [theme]
  )

  const originMarkerIcon = useMemo(
    () =>
      getIcon(theme?.assets?.directionSearch?.origin, originIcon) || (
        <OriginMarkerIcon fontSize="small" sx={originIcon} />
      ),
    [theme, originIcon]
  )

  const destinationMarkerIcon = useMemo(
    () =>
      getIcon(theme?.assets?.directionSearch?.destination, destinationIcon) || (
        <DestinationMarkerIcon
          sx={destinationIcon}
          color="error"
          fontSize="small"
        />
      ),
    [theme, destinationIcon]
  )

  const locationConnectorIcon = useMemo(
    () =>
      getIcon(theme?.assets?.directionSearch?.connector, connectorIcon) || (
        <MoreVertIcon sx={connectorIcon} color="disabled" fontSize="small" />
      ),
    [theme, connectorIcon]
  )

  const switchIcon = useMemo(
    () =>
      getIcon(theme?.assets?.directionSearch?.switch) || (
        <ImportExportIcon fontSize="inherit" />
      ),
    [theme]
  )

  const isRtl = theme.isRtl

  return (
    <Box sx={directionSearchWrapper} onClick={onClick}>
      <Box sx={backIconWrapper}>
        <IconButton
          sx={deepmerge(iconButton, backIconButton)}
          aria-label="back"
          size="small"
          onClick={onBack}
          className={clsx(isRtl && "is-rtl")}
        >
          {backIcon}
        </IconButton>
      </Box>

      <Box sx={directionWrapper}>
        <Box sx={directionIconWrapper}>
          {originMarkerIcon}
          {locationConnectorIcon}
          {destinationMarkerIcon}
        </Box>
        <Box sx={directionInputWrapper}>
          <InputBase
            sx={directionInput}
            placeholder={t("Choose your origin")}
            onChange={onOriginChange}
            onFocus={onOriginFocus}
            inputRef={originInputRef}
            endAdornment={
              <Box sx={endAdornmentWrapper}>
                {endAdornmentSlots.chip?.origin}
                {(endAdornmentSlots.logo.originLogo ||
                  endAdornmentSlots.name.originVenueName) &&
                  originInputRef.current?.value && (
                    <Box
                      sx={venueLogoContainer}
                      className={endAdornmentSlotClasses.origin}
                    >
                      {endAdornmentSlots.logo.originLogo && (
                        <CardMedia
                          sx={venueLogo}
                          image={endAdornmentSlots.logo.originLogo}
                        />
                      )}
                      {endAdornmentSlots.name.originVenueName && (
                        <Typography sx={venueName}>
                          {endAdornmentSlots.name.originVenueName}
                        </Typography>
                      )}
                    </Box>
                  )}
                <ClearButton
                  sx={clearIconButton}
                  aria-label="clear-origin"
                  show={showOriginClearButton}
                  onClick={onClearOrigin}
                >
                  {clearIcon}
                </ClearButton>
              </Box>
            }
            {...props}
          />

          <Divider sx={divider} />

          <InputBase
            sx={directionInput}
            placeholder={t("Choose your destination")}
            onChange={onDestinationChange}
            onFocus={onDestinationFocus}
            inputRef={destinationInputRef}
            endAdornment={
              <Box sx={endAdornmentWrapper}>
                {endAdornmentSlots.chip?.destination}
                {(endAdornmentSlots.logo.destinationLogo ||
                  endAdornmentSlots.name.destinationVenueName) &&
                  destinationInputRef.current?.value && (
                    <Box
                      sx={venueLogoContainer}
                      className={endAdornmentSlotClasses.destination}
                    >
                      {endAdornmentSlots.logo.destinationLogo && (
                        <CardMedia
                          sx={venueLogo}
                          image={endAdornmentSlots.logo.destinationLogo}
                        />
                      )}
                      {endAdornmentSlots.name.destinationVenueName && (
                        <Typography sx={venueName}>
                          {endAdornmentSlots.name.destinationVenueName}
                        </Typography>
                      )}
                    </Box>
                  )}
                <ClearButton
                  sx={clearIconButton}
                  aria-label="clear-destination"
                  show={showDestinationClearButton}
                  onClick={onClearDestination}
                >
                  {clearIcon}
                </ClearButton>
              </Box>
            }
            {...props}
          />
        </Box>
      </Box>

      <Box sx={switchIconWrapper}>
        <IconButton
          sx={deepmerge(iconButton, switchIconButton)}
          aria-label="switch"
          size="small"
          onClick={onSwitch}
        >
          {switchIcon}
        </IconButton>
      </Box>
    </Box>
  )
}
